import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ChevronLeft, Copy, RefreshCw, Info } from 'lucide-react';

/**
 * Owner-only screen: lists revisions that look identical to their parent
 * (same total AND same line items). Read-only — no hide/delete action.
 *
 * Backend: GET /api/proposals/duplicates
 */
function fmtINR(n) {
  if (n == null) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`; }
  catch { return `₹${n}`; }
}

function fmtDT(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ProposalDuplicatesPage() {
  const { user, isOwner } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/proposals/duplicates');
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load duplicates');
      setData({ total: 0, items: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Belt-and-braces guard: the backend already enforces Owner/Admin.
  if (user && !isOwner) return <Navigate to="/proposals" replace />;

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="proposal-duplicates-page">
      <div className="flex items-center justify-between gap-3">
        <Link to="/proposals" className="text-xs text-ink-2 inline-flex items-center gap-1 hover:text-ink">
          <ChevronLeft className="h-3 w-3" /> All proposals
        </Link>
        <Button variant="outline" size="sm" onClick={load} data-testid="duplicates-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card className="shine-card p-5 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-50 border border-amber-200">
            <Copy className="h-5 w-5 text-amber-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-ink">Duplicate revisions</h1>
            <p className="text-sm text-ink-2 mt-1">
              Revisions whose total amount AND line items are identical to the
              previous revision. Read-only view for the Owner. No hide / delete
              action is wired yet — approve a cleanup separately when ready.
            </p>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card className="shine-card p-5 rounded-xl space-y-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </Card>
      ) : (data?.total === 0) ? (
        <Card className="shine-card p-10 rounded-xl text-center" data-testid="duplicates-empty">
          <Info className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
          <div className="text-sm font-semibold text-ink">No duplicate revisions found</div>
          <div className="text-xs text-ink-2 mt-1">
            Every revision has at least one change in total or line items. Nothing to clean up.
          </div>
        </Card>
      ) : (
        <Card className="shine-card rounded-xl overflow-hidden" data-testid="duplicates-list">
          <div className="p-4 border-b border-ink/10 flex items-center justify-between">
            <div className="text-sm font-semibold text-ink">
              {data.total} duplicate revision{data.total === 1 ? '' : 's'} found
            </div>
            <Badge variant="outline" className="text-[11px] text-ink-2">Read-only</Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-ink-2 uppercase border-b border-ink/10 bg-slate-50/60">
                  <th className="py-2 px-3">Proposal #</th>
                  <th className="py-2 px-3">Client</th>
                  <th className="py-2 px-3">Previous rev.</th>
                  <th className="py-2 px-3">Duplicate rev.</th>
                  <th className="py-2 px-3 text-right">Amount</th>
                  <th className="py-2 px-3">Created</th>
                  <th className="py-2 px-3">Why</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row, i) => (
                  <tr key={i} className="border-b border-ink/5 hover:bg-slate-50/40">
                    <td className="py-2 px-3 font-mono text-ink">{row.base_number}</td>
                    <td className="py-2 px-3 text-ink">{row.client_name || '—'}</td>
                    <td className="py-2 px-3">
                      <Link to={`/proposals/${row.parent.id}`} className="text-indigo-700 hover:underline">
                        R{row.parent.revision}
                      </Link>
                      <div className="text-[10px] text-ink-2">{row.parent.status}</div>
                    </td>
                    <td className="py-2 px-3">
                      <Link to={`/proposals/${row.child.id}`} className="text-indigo-700 hover:underline font-semibold">
                        R{row.child.revision}
                      </Link>
                      <div className="text-[10px] text-ink-2">{row.child.status}</div>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-ink">
                      {fmtINR(row.child.total)}
                    </td>
                    <td className="py-2 px-3 text-ink-2">
                      <div>{fmtDT(row.child.created_at)}</div>
                      {row.child.created_by_name && (
                        <div className="text-[10px]">by {row.child.created_by_name}</div>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <ul className="list-disc ml-4 text-ink-2">
                        {(row.reasons || []).map((r, j) => <li key={j}>{r}</li>)}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
